import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { BedDouble, Bath, Ruler, Home, Calendar, Trees, Loader2, MapPin, ExternalLink, Users, BookOpen, Calculator, ArrowRight, TrendingUp, Info, FileText, Lock, Flag, X } from "lucide-react";
import { homesListing, homesValuation, homesSimilarListings, homesReportListing } from "@/lib/api";
import { fmtPrice, fmtNum, typeLabel, photoSrc } from "@/components/homes/homesUtil";
import { PropertyCard } from "@/components/homes/PropertyCard";
import { ListingMap } from "@/components/homes/ListingMap";

export default function PropertyPage() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0); setD(null); setErr(false); setActive(0);
    homesListing(id).then(setD).catch(() => setErr(true));
  }, [id]);

  if (err) return <Center><h1 className="text-3xl text-[#0F172A]">Listing not found</h1><Link to="/homes/search" className="mt-3 text-[#2563EB]">← Back to search</Link></Center>;
  if (!d) return <Center><Loader2 className="h-8 w-8 animate-spin text-[#2563EB]" /></Center>;

  const l = d.listing;
  const rent = l.status === "for_rent";
  const photos = l.photos || [];
  const place = d.place;
  const agent = d.agent;
  const est = d.payment_estimate;

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6" data-testid="property-page">
      {/* Gallery */}
      <div className="grid gap-3 overflow-hidden rounded-2xl md:grid-cols-[2fr_1fr]" data-testid="property-gallery">
        <div className="relative h-72 md:h-[440px]">
          {photos[active] ? (
            <img src={photoSrc(photos[active])} alt={l.address} className="h-full w-full rounded-2xl object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center rounded-2xl bg-[#0F172A]/5 text-[#0F172A]/30"><Home className="h-12 w-12" /></div>
          )}
          <span className={`absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide text-white ${rent ? "bg-[#0F172A]" : "bg-[#2563EB]"}`}>{rent ? "For Rent" : "For Sale"}</span>
          {l.sample && <span className="absolute right-4 top-4 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white/90">Sample listing</span>}
        </div>
        <div className="hidden grid-rows-2 gap-3 md:grid md:h-[440px]">
          {photos.slice(1, 3).map((p, i) => (
            <img key={i} src={photoSrc(p)} alt="" onClick={() => setActive(i + 1)} className="h-full w-full cursor-pointer rounded-2xl object-cover" />
          ))}
        </div>
      </div>
      {photos.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto" data-testid="property-thumbs">
          {photos.map((p, i) => (
            <img key={i} src={photoSrc(p, true)} alt="" onClick={() => setActive(i)}
              className={`h-16 w-24 shrink-0 cursor-pointer rounded-lg object-cover ${active === i ? "ring-2 ring-[#2563EB]" : "opacity-70"}`} />
          ))}
        </div>
      )}
      {photos[0]?.photographer && (
        <div className="mt-1 text-[10px] text-[#334155]/50" data-testid="property-photo-attribution">
          Photos by <a href={photos[0].photographer_url} target="_blank" rel="noreferrer" className="underline">{photos[0].photographer}</a> on <a href={photos[0].unsplash_url} target="_blank" rel="noreferrer" className="underline">Unsplash</a>
        </div>
      )}

      <div className="mt-6 grid gap-8 lg:grid-cols-[1fr_360px]">
        {/* Main */}
        <div>
          {l.headline && <h1 className="mb-1 text-2xl font-black leading-tight text-[#0F172A]" data-testid="property-headline">{l.headline}</h1>}
          <div className="text-4xl font-black text-[#0F172A]" data-testid="property-price">{fmtPrice(l.price, rent)}</div>
          <div className="mt-1 text-lg font-medium text-[#334155]">{l.address}</div>
          <Link to={`/homes/place/${l.city_geo_id}`} className="text-sm text-[#2563EB] hover:underline">{l.city_name}, {l.state_name}</Link>

          <div className="mt-5 flex flex-wrap gap-5 rounded-2xl border border-[#0F172A]/10 bg-white p-5" data-testid="property-facts">
            {l.beds > 0 && <Fact icon={BedDouble} label="Beds" value={l.beds} />}
            {l.baths > 0 && <Fact icon={Bath} label="Baths" value={l.baths} />}
            {l.sqft && <Fact icon={Ruler} label="Sq Ft" value={fmtNum(l.sqft)} />}
            <Fact icon={Home} label="Type" value={typeLabel(l.property_type)} />
            {l.year_built && <Fact icon={Calendar} label="Year built" value={l.year_built} />}
            {l.lot_sqft && <Fact icon={Trees} label="Lot" value={`${fmtNum(l.lot_sqft)} sqft`} />}
          </div>

          <section className="mt-6">
            <h2 className="text-xl font-bold text-[#0F172A]">About this property</h2>
            <p className="mt-2 leading-relaxed text-[#334155]/80">{l.description}</p>
          </section>

          {l.features?.length > 0 && (
            <section className="mt-6">
              <h2 className="text-xl font-bold text-[#0F172A]">Features</h2>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="property-features">
                {l.features.map((f) => <span key={f} className="rounded-full bg-[#0F172A]/5 px-3 py-1.5 text-sm font-medium text-[#0F172A]">{f}</span>)}
              </div>
            </section>
          )}

          <PropertyDetails details={l.details} />

          {/* Map */}
          <section className="mt-6">
            <h2 className="mb-2 text-xl font-bold text-[#0F172A]">Location</h2>
            <ListingMap points={[{ id: l.id, lat: l.latitude, lng: l.longitude, price: l.price, rent, address: l.address }]} singlePin height={340} zoom={13} />
          </section>

          {/* Community intelligence — the HomesPulse difference */}
          {place && (
            <section className="mt-6 rounded-2xl border border-[#0F172A]/10 bg-white p-6" data-testid="property-community">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#2563EB]"><MapPin className="h-4 w-4" /> Know the community</div>
              <h2 className="mt-2 text-2xl font-black text-[#0F172A]">Living in {place.name}</h2>
              {place.description && <p className="mt-2 line-clamp-4 leading-relaxed text-[#334155]/75">{place.description}</p>}
              <div className="mt-4 flex flex-wrap gap-3">
                {place.population != null && <MiniFact label="Population" value={fmtNum(place.population)} />}
                {place.state_name && <MiniFact label="State" value={place.state_name} />}
              </div>
              {place.known_for?.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {place.known_for.slice(0, 6).map((k) => <span key={k} className="rounded-full bg-[#2563EB]/10 px-3 py-1 text-xs font-medium text-[#0F172A]">{k}</span>)}
                </div>
              )}
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to={`/homes/place/${place.geo_id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#0F172A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0F172A]" data-testid="property-city-portal">
                  {place.name} real estate <ArrowRight className="h-4 w-4" />
                </Link>
                {place.url_path && (
                  <Link to={`/place${place.url_path}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#0F172A]/20 px-4 py-2 text-sm font-semibold text-[#0F172A] hover:bg-[#0F172A]/5" data-testid="property-usapulse-link">
                    Full community profile ↗
                  </Link>
                )}
                {place.wikipedia?.available && (
                  <a href={place.wikipedia.wikipedia_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-[#0F172A]/20 px-4 py-2 text-sm font-semibold text-[#0F172A] hover:bg-[#0F172A]/5">
                    <BookOpen className="h-4 w-4" /> Wikipedia <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </section>
          )}

          <OwnerDocuments docs={l.details?.documents} />
        </div>

        {/* Sidebar */}
        <aside className="space-y-5">
          {est && (
            <div className="rounded-2xl border-2 border-[#0F172A]/15 bg-white p-5" data-testid="property-payment">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#2563EB]"><Calculator className="h-4 w-4" /> Est. monthly payment</div>
              <div className="mt-2 text-3xl font-black text-[#0F172A]">${fmtNum(est.monthly_total)}<span className="text-base font-semibold text-[#334155]/50">/mo</span></div>
              <dl className="mt-3 space-y-1.5 text-sm">
                <Row k="Principal & interest" v={`$${fmtNum(est.monthly_pi)}`} />
                <Row k="Property tax (est.)" v={`$${fmtNum(est.monthly_tax)}`} />
                <Row k="HOA" v={`$${fmtNum(est.monthly_hoa)}`} />
                <Row k={`Down payment (20%)`} v={`$${fmtNum(est.down_payment)}`} />
              </dl>
              <p className="mt-3 border-t border-[#0F172A]/10 pt-2 text-[10px] text-[#334155]/50">Estimate only at {est.rate_pct}% / {est.term_years}-yr. Not a loan offer.</p>
            </div>
          )}

          <ValuationCard listingId={id} />

          {agent && (
            <div className="rounded-2xl border border-[#0F172A]/10 bg-white p-5" data-testid="property-agent">
              <div className="mb-3 text-xs font-bold uppercase tracking-widest text-[#0F172A]/50">Listed by</div>
              <div className="flex items-center gap-3">
                {agent.photo?.thumb_url ? (
                  <img src={agent.photo.thumb_url} alt={agent.name} className="h-14 w-14 rounded-full object-cover" />
                ) : (
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#0F172A] text-lg font-black text-white">{agent.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>
                )}
                <div>
                  <Link to={`/homes/agents/${agent.id}`} className="text-lg font-bold text-[#0F172A] hover:text-[#2563EB]">{agent.name}</Link>
                  <div className="text-xs text-[#334155]/60">{agent.brokerage}</div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(agent.specialties || []).map((s) => <span key={s} className="rounded-full bg-[#0F172A]/5 px-2.5 py-1 text-[11px] font-medium text-[#0F172A]">{s}</span>)}
              </div>
              <Link to={`/homes/agents/${agent.id}`} className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-[#2563EB] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1D4ED8]" data-testid="property-contact-agent">
                <Users className="h-4 w-4" /> View agent
              </Link>
            </div>
          )}
        </aside>
      </div>

      <SimilarHomes id={id} />
      <ReportListing id={id} />
    </div>
  );
}

const Center = ({ children }) => <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">{children}</div>;
const Fact = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2"><Icon className="h-5 w-5 text-[#2563EB]" /><span><span className="block text-[10px] uppercase tracking-wider text-[#334155]/50">{label}</span><span className="text-lg font-bold text-[#0F172A]">{value}</span></span></div>
);
const MiniFact = ({ label, value }) => (
  <div className="rounded-lg bg-[#F8FAFC] px-3 py-2"><div className="text-[10px] uppercase tracking-wider text-[#334155]/50">{label}</div><div className="text-base font-bold text-[#0F172A]">{value}</div></div>
);
const Row = ({ k, v }) => <div className="flex justify-between gap-3"><dt className="text-[#334155]/60">{k}</dt><dd className="font-semibold text-[#0F172A]">{v}</dd></div>;


function ValuationCard({ listingId }) {
  const [v, setV] = useState(undefined); // undefined=loading, null=hide
  useEffect(() => {
    let ok = true;
    homesValuation(listingId).then((d) => { if (ok) setV(d && d.ok ? d : null); }).catch(() => ok && setV(null));
    return () => { ok = false; };
  }, [listingId]);

  if (v === undefined || v === null) return null;
  const money = (n) => (typeof n === "number" ? `$${n.toLocaleString()}` : "—");
  return (
    <div className="rounded-2xl border-2 border-[#2563EB]/25 bg-white p-5" data-testid="property-valuation">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#2563EB]"><TrendingUp className="h-4 w-4" /> Market valuation</div>
        <span className="rounded-full bg-[#EEF2FF] px-2 py-0.5 text-[10px] font-bold text-[#2563EB]" data-testid="valuation-source">{v.source}</span>
      </div>
      {v.value?.estimate && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-[#334155]/50">Estimated value</div>
          <div className="text-3xl font-black text-[#0F172A]" data-testid="valuation-value">{money(v.value.estimate)}</div>
          {(v.value.low || v.value.high) && <div className="text-xs text-[#334155]/60">{money(v.value.low)} – {money(v.value.high)}</div>}
        </div>
      )}
      {v.rent?.estimate && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-[#334155]/50">Estimated rent</div>
          <div className="text-xl font-black text-[#0F172A]" data-testid="valuation-rent">{money(v.rent.estimate)}<span className="text-sm font-semibold text-[#334155]/50">/mo</span></div>
        </div>
      )}
      {(v.value?.comps || []).length > 0 && (
        <div className="mt-4 border-t border-[#0F172A]/10 pt-3">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-[#334155]/50">Comparable homes</div>
          <div className="space-y-1.5" data-testid="valuation-comps">
            {v.value.comps.slice(0, 4).map((c, i) => (
              <div key={i} className="flex justify-between gap-2 text-xs">
                <span className="truncate text-[#334155]/70">{c.address || "Nearby home"}{c.beds ? ` · ${c.beds}bd` : ""}</span>
                <span className="shrink-0 font-semibold text-[#0F172A]">{money(c.price)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <p className="mt-3 flex items-start gap-1.5 border-t border-[#0F172A]/10 pt-2 text-[10px] text-[#334155]/50">
        <Info className="mt-0.5 h-3 w-3 shrink-0" />
        {v.estimate
          ? "Rough estimate from HomesPulse sample data. Connect a RentCast key for live public-records valuations."
          : "Live estimate via RentCast public-records data. Not an appraisal."}
      </p>
    </div>
  );
}


// ---- Public property "details" renderer (deep RESO-style fields) ----
const D_LABELS = {
  condition: "Condition", ownership: "Ownership", stories: "Stories", garage_spaces: "Garage spaces",
  lot_size: "Lot size", living_area_source: "Living-area source", occupancy: "Occupancy", possession: "Possession",
  subdivision: "Subdivision", county: "County", mls_number: "MLS #", year_built: "Year built",
  annual_tax: "Annual property tax", tax_year: "Tax year", hoa_fee: "HOA fee", hoa_freq: "HOA frequency",
  concessions: "Seller concessions", deposit: "Security deposit", app_fee: "Application fee",
  lease_duration: "Lease duration", furnished: "Furnished", utilities_included: "Utilities included",
  lease_type: "Lease type", lease_rate_unit: "Lease rate unit", noi: "Net operating income", cap_rate: "Cap rate",
  water_source: "Water source", sewer: "Sewer", electric_provider: "Electric provider", internet: "Internet",
  solar: "Solar panels", solar_ownership: "Solar ownership", flood_zone: "Flood zone", energy_cert: "Energy certification",
  lead_paint: "Lead-based paint", radon: "Radon", other_disclosures: "Other disclosures",
  elementary: "Elementary school", middle_school: "Middle school", high_school: "High school",
  school_district: "School district", school_source: "School data source", transit: "Transit", neighborhood_desc: "Neighborhood",
  land_type: "Land type", acreage: "Total acreage", lot_dimensions: "Lot dimensions", parcels: "Parcels",
  zoning: "Zoning", land_use: "Current land use", permitted_uses: "Permitted uses", dev_status: "Development status",
  subdivision_potential: "Subdivision potential", road_frontage: "Road frontage", road_surface: "Road surface",
  legal_access: "Legal access", easements: "Easements", topography: "Topography", soil_type: "Soil type",
  perc_test: "Perc/soil test", survey: "Survey", boundary_markers: "Boundary markers", rights: "Water/mineral/timber rights",
  irrigation: "Irrigation", crops: "Crops/pasture", fencing: "Fencing", utilities_available: "Utilities available",
  structures: "Structures", ag_exemptions: "Ag exemptions", conservation_easements: "Conservation easements",
  comm_subtype: "Commercial subtype", business_included: "Business included", building_class: "Building class",
  comm_zoning: "Zoning", permitted_use: "Permitted use", current_use: "Current use", building_area: "Building area (sq ft)",
  available_area: "Available area (sq ft)", min_divisible: "Min divisible (sq ft)", max_contiguous: "Max contiguous (sq ft)",
  num_buildings: "Buildings", floors: "Floors", ceiling_height: "Ceiling height (ft)", loading_docks: "Loading docks",
  drive_in_doors: "Drive-in doors", office_area: "Office area (sq ft)", warehouse_area: "Warehouse area (sq ft)",
  parking_spaces: "Parking spaces", parking_ratio: "Parking ratio", traffic_count: "Traffic count", signage: "Signage",
  elevator: "Elevator", sprinkler: "Sprinkler system", rail_access: "Rail access", power_capacity: "Power capacity",
  tenant_improvements: "Tenant improvements", opp_zone: "Opportunity zone", enterprise_zone: "Enterprise zone",
  existing_tenants: "Existing tenants", lease_terms: "Lease terms", sale_leaseback: "Sale-leaseback",
  units: "Residential units", unit_mix: "Unit mix", unit_beds_baths: "Beds/baths per unit", rent_per_unit: "Rent per unit",
  unit_sqft: "Unit square footage", occupancy_rate: "Occupancy rate", lease_expirations: "Lease expirations",
  deposits_held: "Deposits held", gross_income: "Gross rental income", other_income: "Other income",
  op_expenses: "Operating expenses", laundry_income: "Laundry income", parking_income: "Parking income", metering: "Utility metering",
};
const MONEY_KEYS = new Set(["annual_tax", "hoa_fee", "concessions", "deposit", "app_fee", "noi", "gross_income", "other_income", "op_expenses", "deposits_held", "laundry_income", "parking_income", "rent_per_unit"]);
const D_GROUPS = [
  { title: "Home details", keys: ["condition", "ownership", "stories", "garage_spaces", "lot_size", "living_area_source", "occupancy", "possession", "subdivision", "county", "mls_number"] },
  { title: "Costs", keys: ["annual_tax", "tax_year", "hoa_fee", "hoa_freq", "concessions", "deposit", "app_fee", "lease_duration", "furnished", "utilities_included", "lease_type", "lease_rate_unit", "noi", "cap_rate"] },
  { title: "Land & lot", keys: ["land_type", "acreage", "lot_dimensions", "parcels", "zoning", "land_use", "permitted_uses", "dev_status", "subdivision_potential", "road_frontage", "road_surface", "legal_access", "easements", "topography", "soil_type", "perc_test", "survey", "boundary_markers", "rights", "irrigation", "crops", "fencing", "utilities_available", "structures", "ag_exemptions", "conservation_easements"] },
  { title: "Commercial", keys: ["comm_subtype", "business_included", "building_class", "comm_zoning", "permitted_use", "current_use", "building_area", "available_area", "min_divisible", "max_contiguous", "num_buildings", "floors", "ceiling_height", "loading_docks", "drive_in_doors", "office_area", "warehouse_area", "parking_spaces", "parking_ratio", "traffic_count", "signage", "elevator", "sprinkler", "rail_access", "power_capacity", "tenant_improvements", "opp_zone", "enterprise_zone", "existing_tenants", "lease_terms", "sale_leaseback"] },
  { title: "Income & operations", keys: ["units", "unit_mix", "unit_beds_baths", "rent_per_unit", "unit_sqft", "occupancy_rate", "lease_expirations", "deposits_held", "gross_income", "other_income", "op_expenses", "laundry_income", "parking_income", "metering"] },
  { title: "Utilities & environment", keys: ["water_source", "sewer", "electric_provider", "internet", "solar", "solar_ownership", "flood_zone", "energy_cert", "lead_paint", "radon", "other_disclosures"] },
  { title: "Schools & neighborhood", keys: ["elementary", "middle_school", "high_school", "school_district", "school_source", "transit", "neighborhood_desc"] },
];
const isEmpty = (v) => v == null || v === "" || (Array.isArray(v) && v.length === 0) || v === "No" || v === "Not applicable" || v === "Not done";
function fmtDetail(key, v) {
  if (Array.isArray(v)) return v.join(", ");
  if (MONEY_KEYS.has(key) && typeof v === "number") return `$${v.toLocaleString()}`;
  return String(v);
}
function PropertyDetails({ details }) {
  if (!details || typeof details !== "object") return null;
  const groups = D_GROUPS.map((g) => ({
    title: g.title,
    rows: g.keys.filter((k) => !isEmpty(details[k])).map((k) => [D_LABELS[k] || k, fmtDetail(k, details[k])]),
  })).filter((g) => g.rows.length > 0);
  if (groups.length === 0) return null;
  return (
    <section className="mt-6" data-testid="property-details">
      <h2 className="text-xl font-bold text-[#0F172A]">Property details</h2>
      <div className="mt-3 space-y-5">
        {groups.map((g) => (
          <div key={g.title}>
            <div className="mb-2 text-[11px] font-bold uppercase tracking-widest text-[#334155]/50">{g.title}</div>
            <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
              {g.rows.map(([label, val]) => (
                <div key={label} className="flex justify-between gap-3 border-b border-[#0F172A]/5 pb-1.5">
                  <dt className="text-sm text-[#334155]/70">{label}</dt>
                  <dd className="text-sm font-semibold text-[#0F172A] text-right">{val}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </section>
  );
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Owner-only: private documents (rent roll / P&L) are returned only to the owner.
function OwnerDocuments({ docs }) {
  if (!Array.isArray(docs) || docs.length === 0) return null;
  return (
    <section className="mt-6 rounded-2xl border border-[#2563EB]/20 bg-[#EEF2FF]/40 p-6" data-testid="owner-documents">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#2563EB]"><Lock className="h-4 w-4" /> Your private documents</div>
      <p className="mt-1 text-sm text-[#334155]/70">Only you can see and download these — they are never shown to the public.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {docs.map((doc) => (
          <a key={doc.path || doc.url || doc.name} href={doc.url?.startsWith("http") ? doc.url : `${BACKEND}${doc.url}`} target="_blank" rel="noreferrer"
            className="flex items-center gap-2 rounded-full border border-[#2563EB]/30 bg-white px-3.5 py-2 text-sm font-semibold text-[#0F172A] hover:border-[#2563EB]" data-testid="owner-doc-link">
            <FileText className="h-4 w-4 text-[#2563EB]" /> {doc.name || "Document"} <ExternalLink className="h-3 w-3 text-[#334155]/40" />
          </a>
        ))}
      </div>
    </section>
  );
}

function SimilarHomes({ id }) {
  const [items, setItems] = useState(null);
  useEffect(() => { setItems(null); homesSimilarListings(id).then((d) => setItems(d.listings || [])).catch(() => setItems([])); }, [id]);
  if (!items || items.length === 0) return null;
  return (
    <section className="mt-10" data-testid="similar-homes">
      <h2 className="text-2xl font-black text-[#0F172A]">Similar properties</h2>
      <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {items.slice(0, 6).map((l) => <PropertyCard key={l.id} listing={l} />)}
      </div>
    </section>
  );
}

function ReportListing({ id }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const REASONS = ["Incorrect price", "Incorrect details", "Wrong photos", "No longer available", "Suspected fraud / scam", "Other"];
  const submit = async () => {
    if (!reason) { toast.error("Please choose a reason."); return; }
    setSending(true);
    try {
      const r = await homesReportListing(id, { reason, details, email });
      toast.success(r.message || "Thank you — we'll review this listing.");
      setOpen(false); setReason(""); setDetails(""); setEmail("");
    } catch { toast.error("Couldn't submit your report. Please try again."); }
    finally { setSending(false); }
  };
  return (
    <>
      <div className="mt-8 flex justify-center border-t border-[#0F172A]/10 pt-6">
        <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#334155]/60 hover:text-[#c8102e]" data-testid="report-open">
          <Flag className="h-4 w-4" /> Report incorrect information
        </button>
      </div>
      <p className="mx-auto mt-4 max-w-3xl px-4 text-center text-[11px] leading-relaxed text-[#334155]/45">
        Property information is provided by the listing participant or identified data source and is deemed reliable but is not guaranteed. Buyers and renters should independently verify property details, measurements, schools, taxes, zoning, permits, environmental conditions and availability. Listings are subject to change, withdrawal or prior sale or rental without notice. HomesPulse is an Equal Housing Opportunity platform.
      </p>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" data-testid="report-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between">
              <h3 className="text-xl font-black text-[#0F172A]">Report this listing</h3>
              <button onClick={() => setOpen(false)} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100" data-testid="report-close"><X className="h-5 w-5" /></button>
            </div>
            <label className="mt-4 block text-xs font-bold uppercase tracking-widest text-slate-500">Reason</label>
            <select value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" data-testid="report-reason">
              <option value="">Select a reason…</option>
              {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <label className="mt-3 block text-xs font-bold uppercase tracking-widest text-slate-500">Details (optional)</label>
            <textarea rows={3} value={details} onChange={(e) => setDetails(e.target.value)} className="mt-1.5 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" data-testid="report-details" />
            <label className="mt-3 block text-xs font-bold uppercase tracking-widest text-slate-500">Your email (optional)</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" data-testid="report-email" />
            <button onClick={submit} disabled={sending} className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-[#c8102e] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#a60d26] disabled:opacity-60" data-testid="report-submit">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Flag className="h-4 w-4" />} Submit report
            </button>
          </div>
        </div>
      )}
    </>
  );
}

